/**
 * Foundation Context - registro dei form "dirty".
 *
 * Ogni componente che gestisce modifiche non salvate puo' registrarsi qui.
 * Prima di un cambio contesto la Foundation interroga il registro e, se
 * risultano form sporchi, apre `UnsavedChangesDialog`.
 *
 * L'API e' volutamente semplice per evitare accoppiamenti: chi si registra
 * fornisce un id + un callback opzionale `onDiscard()` (chiamato quando
 * l'utente sceglie "Scarta e continua").
 */
import { useEffect } from "react";

const _dirty = new Map();
const _listeners = new Set();

function _notify() {
  _listeners.forEach((fn) => {
    try { fn(_dirty.size > 0); } catch { /* noop */ }
  });
}

export function registerDirty(id, meta = {}) {
  _dirty.set(id, { id, ...meta, since: Date.now() });
  _notify();
}

export function unregisterDirty(id) {
  _dirty.delete(id);
  _notify();
}

export function isAnyDirty() {
  return _dirty.size > 0;
}

export function listDirty() {
  return Array.from(_dirty.values());
}

export function subscribeDirty(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export function discardAllDirty() {
  const items = listDirty();
  items.forEach((it) => {
    try { it.onDiscard?.(); } catch { /* noop */ }
  });
  _dirty.clear();
  _notify();
}

/**
 * Hook di comodita': registra il componente come "dirty" solo se `active`.
 * Al dismount elimina automaticamente l'entry.
 *
 * Uso:
 *   const isDirty = useMemo(...);
 *   useDirtyRegistrar("attendance-form", isDirty, { onDiscard: reset });
 */
export function useDirtyRegistrar(id, active, meta) {
  useEffect(() => {
    if (active) registerDirty(id, meta || {});
    else unregisterDirty(id);
    return () => unregisterDirty(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, active]);
}
